import React, { useState, useEffect } from 'react';
import { Button, Form, InputGroup } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { Classification } from '../../../types/Employee';
import type { ServiceArea } from '../../../services/areasService';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';

interface ClassificationModalProps {
  show: boolean;
  onHide: () => void;
  classification: Classification | null;
  serviceAreas: ServiceArea[];
  onSave: (classificationData: Partial<Classification>) => Promise<void>;
}

const ClassificationModal: React.FC<ClassificationModalProps> = ({
  show,
  onHide,
  classification,
  serviceAreas,
  onSave
}) => {
  const [formData, setFormData] = useState({
    code: '',
    rate: '',
    serviceAreaId: 0
  });
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  
  // Check if user is admin
  const isAdmin = hasPermission('user:edit') || hasPermission('user:create') || currentUser?.role?.name === 'Admin';
  const isReadOnly = !hasPermission('classification:edit') && !isAdmin;

  useEffect(() => {
    if (show) {
      if (classification) {
        setFormData({
          code: classification.code || '',
          rate: classification.rate ? classification.rate.toFixed(2) : '',
          serviceAreaId: classification.serviceAreaId || 0
        });
      } else {
        setFormData({
          code: '',
          rate: '',
          serviceAreaId: 0
        });
      }
    }
  }, [show, classification]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;

    if (!formData.code || !formData.rate || !formData.serviceAreaId) {
      alert('Please fill in all required fields');
      return;
    }

    const rate = parseFloat(formData.rate);
    if (isNaN(rate) || rate < 0) {
      alert('Please enter a valid rate');
      return;
    }

    try {
      await onSave({
        code: formData.code,
        rate: rate,
        serviceAreaId: formData.serviceAreaId
      });
      onHide();
    } catch (error: any) {
      console.error('Error saving classification:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          'Failed to save classification. Please try again.';
      alert(errorMessage);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={classification ? 'Edit Classification' : 'Add Classification'}
      size="medium"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={handleCancel} className="tw-mr-2">
            Cancel
          </Button>
          {!isReadOnly && (
            <Button variant="primary" type="submit" form="classificationForm">
              {classification ? 'Save Changes' : 'Create Classification'}
            </Button>
          )}
        </>
      }
    >
      <Form onSubmit={handleSubmit} id="classificationForm">
        <Form.Group className="tw-mb-3">
          <Form.Label>Code <span className="tw-text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.code}
            onChange={e => !isReadOnly && setFormData({...formData, code: e.target.value})}
            readOnly={isReadOnly}
            required
            placeholder="e.g., CL1, CL2"
          />
          <Form.Text className="tw-text-content-muted">Classification code (not unique)</Form.Text>
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Rate <span className="tw-text-danger">*</span></Form.Label>
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={formData.rate}
              onChange={e => !isReadOnly && setFormData({...formData, rate: e.target.value})}
              onBlur={e => {
                // Format to 2 decimal places on blur
                if (e.target.value && !isNaN(parseFloat(e.target.value))) {
                  const numValue = parseFloat(e.target.value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    setFormData({...formData, rate: numValue.toFixed(2)});
                  }
                }
              }}
              readOnly={isReadOnly}
              required
              placeholder="0.00"
            />
          </InputGroup>
          <Form.Text className="tw-text-content-muted">Pay rate for this classification</Form.Text>
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Service Area <span className="tw-text-danger">*</span></Form.Label>
          <Form.Select
            value={formData.serviceAreaId}
            onChange={e => !isReadOnly && setFormData({...formData, serviceAreaId: parseInt(e.target.value)})}
            disabled={isReadOnly}
            required
          >
            <option value="0">Select a service area</option>
            {serviceAreas.map(area => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default ClassificationModal;
